// useThreadsFeed — Threads activity feed.

import { useCallback, useMemo, useSyncExternalStore } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";
import {
  sendThreadReadReceipt,
  subscribeThreadsFeed,
  type Client,
  type ThreadsFeedItem,
} from "~/lib/matrix";

export interface UseThreadsFeedResult {
  items: ThreadsFeedItem[];
  totalUnread: number;
  isLoading: boolean;
  settled: boolean;
  markAllRead: () => Promise<void>;
  markThreadRead: (item: ThreadsFeedItem) => Promise<void>;
}

type FeedSnapshot = { items: ThreadsFeedItem[]; settled: boolean };

const EMPTY_SNAPSHOT: FeedSnapshot = Object.freeze({ items: [], settled: false });

interface ThreadFeedStore {
  getSnapshot(): FeedSnapshot;
  subscribe(listener: () => void): () => void;
}

const stores = new WeakMap<Client, ThreadFeedStore>();

function makeThreadFeedStore(client: Client): ThreadFeedStore {
  let snapshot: FeedSnapshot = { items: [], settled: false };
  const listeners: Set<() => void> = new Set();
  let generation = 0;
  let unsubscribe: (() => void) | null = null;

  const notify = () => {
    for (const listener of listeners) listener();
  };

  const start = () => {
    const seen = generation;
    unsubscribe = subscribeThreadsFeed(client, (next, meta) => {
      if (generation !== seen) return;
      snapshot = { items: next, settled: meta.settled };
      notify();
    });
  };

  const dispose = () => {
    generation += 1;
    unsubscribe?.();
    unsubscribe = null;
    snapshot = { items: [], settled: false };
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    if (unsubscribe === null) start();
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) dispose();
    };
  };

  return {
    getSnapshot: () => snapshot,
    subscribe,
  };
}

function threadFeedStore(client: Client): ThreadFeedStore {
  let store = stores.get(client);
  if (!store) {
    store = makeThreadFeedStore(client);
    stores.set(client, store);
  }
  return store;
}

function useRawFeed(client: Client | null | undefined): FeedSnapshot {
  const store = useMemo(() => (client ? threadFeedStore(client) : null), [client]);
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => {}),
    [store],
  );
  const getSnapshot = useCallback(() => (store ? store.getSnapshot() : EMPTY_SNAPSHOT), [store]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useThreadsFeed(): UseThreadsFeedResult {
  const { client, state } = useMatrix();
  const rooms = useRooms();
  const { items: rawItems, settled } = useRawFeed(client ?? undefined);

  const visibleItems = useMemo(() => {
    const visibleRoomIds = new Set(rooms.map((room) => room.id));
    return rawItems.filter((item) => visibleRoomIds.has(item.roomId));
  }, [rawItems, rooms]);

  const totalUnread = useMemo(() => {
    let total = 0;
    for (const item of visibleItems) total += item.unreadReplyCount;
    return total;
  }, [visibleItems]);

  const markAllRead = async () => {
    if (!client) return;
    const snapshot = visibleItems;
    await Promise.all(
      snapshot.map(async (item) => {
        if (item.unreadReplyCount <= 0) return;
        await sendThreadReadReceipt(client, item.roomId, item.rootEventId, item.lastReplyEventId);
      }),
    );
  };

  const markThreadRead = async (item: ThreadsFeedItem) => {
    if (!client) return;
    if (item.unreadReplyCount <= 0) return;
    await sendThreadReadReceipt(client, item.roomId, item.rootEventId, item.lastReplyEventId);
  };

  const clientLoading = state === "loading" || state === "syncing";

  return {
    items: visibleItems,
    totalUnread,
    isLoading: clientLoading || !settled,
    settled,
    markAllRead,
    markThreadRead,
  };
}
